import { BitkitControlButton, BitkitPageHeader, BitkitPageHeaderProps, IconMenuHamburger } from '@bitrise/bitkit-v2';
import { Box, BoxProps } from '@chakra-ui/react/box';
import { ReactNode, useRef, useState } from 'react';

import InternalFooter from '@/components/InternalFooter/InternalFooter';
import PageHeaderBreadcrumb, { PageHeaderBreadcrumbItem } from '@/components/page/PageHeader/PageHeaderBreadcrumb';
import StickyPageHeader from '@/components/page/PageHeader/StickyPageHeader';
import { HEADER_HEIGHT, NAV_WIDTH } from '@/constants';

import PageLayoutSidebar, { PageLayoutSidebarProps } from './PageLayoutSidebar';
import useIsSidebarOpen from './useIsSidebarOpen';

export type PageLayoutProps = {
  children: ReactNode;
  footerBackground?: BoxProps['background'];
  showFooter?: boolean;
  header: Pick<BitkitPageHeaderProps, 'avatar' | 'badge' | 'contentVariant' | 'secondaryText' | 'tabs' | 'title'> & {
    breadcrumbData?: PageHeaderBreadcrumbItem[];
    /** Trailing actions. Called with the header's sticky state so they can drop to `size="sm"` in the stuck row. */
    children?: (props: { isSticky: boolean }) => ReactNode;
    clarityUnmask?: boolean;
    colorScheme?: 'gray' | 'white';
    /** Rendered below the header, inside the same block — banners and notifications that scroll with it. */
    extraContent?: ReactNode;
    /**
     * Caps the header's content and centres it, while its background and line still run the full
     * width. Includes the header's 32 inline padding; centre the page's content at the same width.
     */
    contentWidth?: BitkitPageHeaderProps['contentWidth'];
    shouldSticky?: boolean;
    showDivider?: boolean;
  };
  isMobile: boolean;
  platformShell?: {
    headerHeight: number;
    navWidth: number;
  };
  sidebar?: Omit<
    PageLayoutSidebarProps,
    'isMobile' | 'platformShell' | 'width' | 'isStickyHeader' | 'pageHeaderRef'
  > & {
    width?: PageLayoutSidebarProps['width'];
  };
};

const PageLayout = ({
  children,
  footerBackground,
  showFooter = true,
  header,
  isMobile,
  platformShell = {
    headerHeight: HEADER_HEIGHT,
    navWidth: NAV_WIDTH,
  },
  sidebar,
}: PageLayoutProps) => {
  const {
    breadcrumbData,
    children: headerActions,
    clarityUnmask,
    colorScheme,
    extraContent,
    contentWidth,
    shouldSticky = true,
    showDivider,
    title,
    ...headerRest
  } = header;

  const pageHeaderRef = useRef<HTMLDivElement>(null);

  const [isStickyHeader, setIsStickyHeader] = useState(false);

  const { isSidebarOpen, setIsSidebarOpen } = useIsSidebarOpen();

  const pageLayoutSidebarWidth = sidebar?.width ? sidebar.width : '16rem';

  const unMaskHeader = clarityUnmask !== false;

  const navToggle = sidebar && isMobile && !isSidebarOpen && (
    <BitkitControlButton
      icon={IconMenuHamburger}
      label="Open navigation"
      onClick={() => setIsSidebarOpen(true)}
      size="xs"
    />
  );

  const renderTrail = (isSticky: boolean) =>
    breadcrumbData?.length ? (
      <PageHeaderBreadcrumb currentTitle={title} data={breadcrumbData} isSticky={isSticky} />
    ) : undefined;

  return (
    <>
      <Box data-clarity-unmask={unMaskHeader} data-testid="page-layout-header" ref={pageHeaderRef}>
        <StickyPageHeader onStickyChange={setIsStickyHeader} shouldSticky={shouldSticky && !isMobile}>
          {(isSticky) => {
            const trail = renderTrail(isSticky);

            return (
              <BitkitPageHeader
                {...headerRest}
                contentWidth={contentWidth}
                breadcrumb={
                  navToggle ? (
                    // Interim: the mobile nav toggle rides in the breadcrumb slot so it sits in the trail's
                    // row, as it did on master -- BitkitPageHeader has no slot of its own for it.
                    <Box alignItems="center" display="flex" gap="8" minWidth="0">
                      {navToggle}
                      {trail}
                    </Box>
                  ) : (
                    trail
                  )
                }
                isSticky={isSticky}
                showDivider={showDivider}
                title={title}
              >
                {headerActions?.({ isSticky })}
              </BitkitPageHeader>
            );
          }}
        </StickyPageHeader>
        {extraContent && (
          // The header used to pad everything under it; BitkitPageHeader pads only itself, so what
          // hangs below it -- the billing alerts -- takes the same inset to line up with it.
          <Box paddingInlineEnd={{ base: '16', tablet: '32' }} paddingInlineStart={{ base: '16', tablet: '24' }}>
            {extraContent}
          </Box>
        )}
      </Box>
      <Box flex="1" display="flex" flexDirection={isMobile ? 'column' : 'row'}>
        {sidebar && (isSidebarOpen || !isMobile) && (
          <PageLayoutSidebar
            {...sidebar}
            isStickyHeader={isStickyHeader}
            width={pageLayoutSidebarWidth}
            isMobile={isMobile}
            platformShell={platformShell}
            pageHeaderRef={pageHeaderRef}
          />
        )}
        <Box
          display="flex"
          flex="1"
          flexDir="column"
          maxW={['unset', sidebar ? `calc(100% - ${pageLayoutSidebarWidth})` : 'unset']}
        >
          {(!isMobile || !isSidebarOpen) && (
            <Box flex="1" display="flex" flexDirection="column">
              {children}
            </Box>
          )}
          {showFooter && (
            <InternalFooter
              bg={colorScheme === 'gray' ? 'background/primary' : footerBackground}
              marginTop="auto"
              paddingLeft="12"
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default PageLayout;
